import React, { useState, useEffect, useRef, useCallback } from "react";
import { Excalidraw, exportToSvg } from "@excalidraw/excalidraw";

import throttle from "lodash.throttle";
import { useMeeting } from "@videosdk.live/react-sdk";
import { useMeetingAppContext } from "../../../hooks/MeetingAppContextDef";
import { useWhiteboardRecording } from "../../../hooks/useWhiteboardRecording";
import {
  getWhiteboardSocket,
  resetWhiteboardSocket,
  subscribeToWhiteboardSocket,
} from "../../../hooks/whiteboardSocket";
// Add alongside your other imports, at the top of the file
import whiteLogoImg from "../../../assets/images/needle-in-the-haystack-white-logo.svg";
import removeWhiteImg from "../../../assets/images/remove-icon.svg";
import rightImg from "../../../assets/images/right-white-icon.svg";
import "./WhiteboardView.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
// Socket events — mirrors excalidrawSync-poc.js constants
const WB_UPDATE = "whiteboard-update";
const WB_SYNC = "whiteboard-sync";
const WB_CLEAR = "whiteboard-clear";
const WB_JOIN = "join-board";

const WB_RECONNECT = "whiteboard-reconnect";

export function WhiteboardView({
  sessionId,
  meetingId,
  exportRef,
  clearRef,
  onSubmit,
  setShowWhiteboard,
  whiteboardQuestions,
  questionIndex = 0,
  isReconnect = false,
  reconnectQuestionId = null,
  reconnectElapsedSeconds = 0,
  // 0-based index of the last video chunk successfully streamed before the
  // disconnect (pre-reconnection-check's "whiteboarding_video_index").
  // Passed through to useWhiteboardRecording so chunk numbering resumes at
  // reconnectVideoIndex + 1 instead of restarting at 0. null on a fresh
  // (non-reconnect) session.
  reconnectVideoIndex = null,
}) {
  const { setSideBarMode } = useMeetingAppContext();
  const { localMicOn, toggleMic } = useMeeting();
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  // ── Excalidraw API ref ────────────────────────────────────────────────────
  const excalidrawAPI = useRef(null);
  const isRemoteUpdateRef = useRef(false);
  // Holds the elements array from a "whiteboard-reconnect" payload that
  // arrives before Excalidraw has mounted (excalidrawAPI.current still
  // null) — flushed once the excalidrawAPI ref callback fires below.
  const pendingReconnectElementsRef = useRef(null);
  const [finalSubmitting, setIsFinalSubmitting] = useState(false);
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false);
  // ── Whiteboard container ref (used by useWhiteboardRecording for html2canvas) ──
  const whiteboardContainerRef = useRef(null);

  const [isQuestionPanelOpen, setIsQuestionPanelOpen] = useState(true);
  const [isPinned, setIsPinned] = useState(true);
  const questionPanelRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !isPinned &&
        isQuestionPanelOpen &&
        questionPanelRef.current &&
        !questionPanelRef.current.contains(event.target) &&
        !event.target.closest(".view-question-btn")
      ) {
        setIsQuestionPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPinned, isQuestionPanelOpen]);

  // ── Current question / last-question tracking ─────────────────────────────
  // (Computed here, ahead of the socket below, since the socket URL now
  // needs questionId too.)
  const totalQuestions = whiteboardQuestions?.length || 1;
  const currentQuestion =
    whiteboardQuestions?.[questionIndex] ?? whiteboardQuestions?.[0];
  const isLastQuestion = questionIndex >= totalQuestions - 1;

  const questionId =
    (isReconnect && reconnectQuestionId) ||
    currentQuestion?.question_id ||
    null;

  // ── Socket (reactive) ──────────────────────────────────────────────────
  // getWhiteboardSocket() reads/writes a module-level singleton in
  // whiteboardSocket.js. Reading it directly in the render body (the old
  // approach) meant React never found out when a network drop caused the
  // singleton to be silently replaced with a brand-new WebSocket — this
  // component would keep using the dead socket (buttons doing nothing,
  // remote sync stalled) until some UNRELATED re-render happened to pick
  // up the new reference. Subscribing instead guarantees an immediate
  // re-render the moment the socket actually changes.
  const skipReconnect = finalSubmitting || showSubmitSuccess;
  const [socket, setSocket] = useState(() =>
    getWhiteboardSocket(sessionId, questionId, skipReconnect),
  );

  useEffect(() => {
    // Re-request (may create a fresh socket if boardId/questionId/skip
    // changed or the current one is dead) and sync state immediately...
    setSocket(getWhiteboardSocket(sessionId, questionId, skipReconnect));
    // ...then stay subscribed so a reconnect that happens later (mid-
    // session, with none of these deps changing) still propagates here.
    const unsubscribe = subscribeToWhiteboardSocket(setSocket);
    return unsubscribe;
  }, [sessionId, questionId, skipReconnect]);

  useEffect(() => {
    return () => {
      // resetWhiteboardSocket();
    };
  }, []);
  // ── Open count (Round N badge) ────────────────────────────────────────────
  const [openCount] = useState(() => {
    const prev = parseInt(
      sessionStorage.getItem("whiteboardOpenCount") || "0",
      10,
    );
    const next = (isNaN(prev) ? 0 : prev) + 1;
    sessionStorage.setItem("whiteboardOpenCount", String(next));
    return next;
  });

  // ── Snackbar "Click Submit when done" ─────────────────────────────────────
  const [showSnackbar, setShowSnackbar] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowSnackbar(false), 10000);
    return () => clearTimeout(t);
  }, []);

  // ── Countdown timer (15 minutes = 900 s) ──────────────────────────────────
  const TIMER_DURATION_S = 15 * 60; // change this constant to adjust the limit

  const [timeLeft, setTimeLeft] = useState(() => {
    if (!isReconnect) return TIMER_DURATION_S;
    const elapsed = Math.max(0, Math.round(reconnectElapsedSeconds || 0));
    return Math.max(0, TIMER_DURATION_S - elapsed);
  });
  const autoSubmittedRef = useRef(false);

  useEffect(() => {
    if (timeLeft <= 0) return;
    const tick = setInterval(() => setTimeLeft((s) => s - 1), 1000);
    return () => clearInterval(tick);
  }, [timeLeft]);
  useEffect(() => {
    if (!meetingId || !socket) return;
    socket.connect();

    if (isReconnect) {
      socket.emit(WB_RECONNECT, {
        roomId: meetingId,
        boardId: sessionId,
        whiteboardQuestionId: questionId,
      });
    } else {
      socket.emit(WB_JOIN, {
        roomId: meetingId,
        boardId: sessionId,
        questionId,
      });
    }

    socket.emit("start-recording", {
      sessionId,
      roomId: meetingId,
      boardId: sessionId,
      questionId,
    });
  }, [meetingId, sessionId, socket, isReconnect, questionId]);

  // Auto-submit when the clock hits zero
  useEffect(() => {
    if (timeLeft <= 0 && !autoSubmittedRef.current) {
      autoSubmittedRef.current = true;
      handleSubmit(); // reuses the same submit flow
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeft]);

  const formatTime = (s) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, "0");
    const sec = (s % 60).toString().padStart(2, "0");
    return `${m}:${sec}`;
  };

  // Timer turns red + pulses in the last 30 seconds
  const timerIsUrgent = timeLeft <= 30;

  // ── Ensure mic is on ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!localMicOn) toggleMic();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Export whiteboard as SVG Blob ─────────────────────────────────────────
  const exportWhiteboard = useCallback(async () => {
    if (!excalidrawAPI.current) {
      console.warn("[WhiteboardView] Excalidraw API not ready");
      return null;
    }

    const elements = excalidrawAPI.current.getSceneElements();
    const appState = excalidrawAPI.current.getAppState();

    const svgElement = await exportToSvg({
      elements,
      appState: {
        ...appState,
        exportWithDarkMode: false,
        exportBackground: true,
      },
      files: excalidrawAPI.current.getFiles(),
    });

    const svgString = new XMLSerializer().serializeToString(svgElement);
    return new Blob([svgString], { type: "image/svg+xml" });
  }, []);

  // Expose exportWhiteboard to parent (MeetingContainer reads this ref)
  useEffect(() => {
    if (exportRef) exportRef.current = exportWhiteboard;
  }, [exportRef, exportWhiteboard]);

  // ── Clear board ───────────────────────────────────────────────────────────
  const clearBoard = useCallback(() => {
    if (!excalidrawAPI.current) return;
    excalidrawAPI.current.updateScene({ elements: [] });
    socket.emit(WB_CLEAR, { roomId: meetingId, boardId: sessionId });
  }, [meetingId, sessionId, socket]);

  // Expose clearBoard to parent (called after successful submit)
  useEffect(() => {
    if (clearRef) clearRef.current = clearBoard;
  }, [clearRef, clearBoard]);

  // Note: joining the room already happens in the effect above (the one
  // that also handles start-recording / WB_RECONNECT) — a second WB_JOIN
  // emit here was firing every time the socket changed, double-joining
  // the room on every mount and every reconnect.

  // ── Socket: receive remote updates ────────────────────────────────────────
  useEffect(() => {
    if (!socket) return;
    // whiteboard-update/-sync payloads come wrapped as
    // { roomId, boardId, elements: [...] } on the wire — but tolerate a
    // raw array too, in case a given event ever sends elements directly.
    const extractElements = (payload) =>
      Array.isArray(payload) ? payload : payload?.elements;

    const applyRemote = (payload) => {
      const elements = extractElements(payload);
      if (!excalidrawAPI.current || !Array.isArray(elements)) return;
      isRemoteUpdateRef.current = true;
      excalidrawAPI.current.updateScene({ elements });
      requestAnimationFrame(() => {
        isRemoteUpdateRef.current = false;
      });
    };

    const handleClear = () => applyRemote([]);

    // whiteboard-reconnect: server's reply to the WB_RECONNECT request
    // above, carrying the board's full current state as a raw element
    // array. If Excalidraw hasn't mounted yet, stash it and the
    // excalidrawAPI ref callback below will flush it once ready.
    const applyReconnectState = (payload) => {
      const elements = extractElements(payload) || [];
      if (!excalidrawAPI.current) {
        pendingReconnectElementsRef.current = elements;
        return;
      }
      isRemoteUpdateRef.current = true;
      excalidrawAPI.current.updateScene({ elements });
      requestAnimationFrame(() => {
        isRemoteUpdateRef.current = false;
      });
    };

    socket.on(WB_UPDATE, applyRemote);
    socket.on(WB_SYNC, applyRemote);
    socket.on(WB_CLEAR, handleClear);
    socket.on(WB_RECONNECT, applyReconnectState);

    return () => {
      socket.off(WB_UPDATE, applyRemote);
      socket.off(WB_SYNC, applyRemote);
      socket.off(WB_CLEAR, handleClear);
      socket.off(WB_RECONNECT, applyReconnectState);
    };
  }, [socket]);

  // ── Throttled emit of local changes ──────────────────────────────────────
  const lastVersionRef = useRef(0);
  const socketRef = useRef(socket);
  useEffect(() => {
    socketRef.current = socket;
  }, [socket]);

  const meetingIdRef = useRef(meetingId);
  const sessionIdRef = useRef(sessionId);
  useEffect(() => {
    meetingIdRef.current = meetingId;
    sessionIdRef.current = sessionId;
  }, [meetingId, sessionId]);

  const emitChangesRef = useRef(null);
  useEffect(() => {
    emitChangesRef.current = throttle((elements) => {
      const activeSocket = socketRef.current;
      if (!activeSocket) return;
      activeSocket.emit(WB_UPDATE, {
        roomId: meetingIdRef.current,
        boardId: sessionIdRef.current,
        elements,
      });
    }, 100);

    return () => {
      emitChangesRef.current?.cancel?.();
    };
  }, []);

  const handleChange = useCallback((elements) => {
    if (isRemoteUpdateRef.current) return;

    if (Array.isArray(elements)) {
      let versionSum = elements.length;
      for (let i = 0; i < elements.length; i++) {
        versionSum +=
          (elements[i].version || 0) + (elements[i].isDeleted ? 1 : 0);
      }
      if (versionSum === lastVersionRef.current) return;
      lastVersionRef.current = versionSum;
    }

    emitChangesRef.current?.(elements);
  }, []);

  // ── Recording hook ────────────────────────────────────────────────────────
  // snapshotRef wraps exportWhiteboard in the same toBlob interface that
  // useWhiteboardRecording expects (so it can call canvas.toBlob).
  const snapshotRef = useRef(null);
  useEffect(() => {
    snapshotRef.current = {
      toBlob: (callback, _type, _quality) => {
        exportWhiteboard()
          .then((blob) => callback(blob))
          .catch((err) => {
            console.warn("[WhiteboardView] SVG export failed:", err);
            callback(null);
          });
      },
    };
  }, [exportWhiteboard]);

  const interviewId = sessionStorage.getItem("interviewId") || "";
  const {
    startWhiteboardRecording,
    stopAndSubmitWhiteboard,
    handOffToNextQuestion,
    isSubmitting,
    error: recordingError,
  } = useWhiteboardRecording({
    interviewId,
    canvasRef: snapshotRef,
    // NEW: pass the whiteboard container div ref so html2canvas can capture it
    containerRef: whiteboardContainerRef,
    // NEW: pass sessionId so chunked uploads go to the right server session folder
    sessionId,
    // NEW: pass the whiteboard socket so each chunk (including the final,
    // possibly-shorter one on submit) streams out as its own independent
    // "recording-chunk" event — there is no merged/HTTP upload anymore.
    socket,
    // NEW: on a whiteboard-reconnect, resume chunk numbering right after
    // the last chunk the server already has, instead of restarting at 0.
    startChunkIndex: isReconnect ? reconnectVideoIndex : null,
  });

  // Start audio + video recording silently on mount, and play question audio if available
  const audioRef = useRef(null);

  useEffect(() => {
    startWhiteboardRecording();

    if (currentQuestion?.audio_base64) {
      const audio = new Audio(
        `data:audio/wav;base64,${currentQuestion.audio_base64}`,
      );

      audioRef.current = audio;
      audio.play().catch(console.warn);
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    };
  }, [currentQuestion?.question_id]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  // Only used on the LAST question — hides the whiteboard panel and hands
  // control back to MeetingContainer's normal end-of-whiteboard flow
  // (release LST / reconnect thank-you audio / etc).
  const closeWhiteboard = () => {
    setShowWhiteboard(false);
    setSideBarMode("CHAT");
    if (onSubmit) onSubmit({ isLastQuestion: true, questionIndex });
  };

  const handleSubmit = async () => {
    setIsFinalSubmitting(true);
    try {
      const svgBlob = await exportWhiteboard();
      const snapshot = svgBlob ? await svgBlob.text() : null;

      if (!isLastQuestion) {
        setTimeLeft(TIMER_DURATION_S);
        await handOffToNextQuestion();

        // Emit stop-recording for this question (snapshot only, no video)
        socket.emit("stop-recording", {
          sessionId,
          roomId: meetingId,
          boardId: sessionId,
          snapshot,
        });
      } else {
        await stopAndSubmitWhiteboard();

        socket.emit("stop-recording", {
          sessionId,
          roomId: meetingId,
          boardId: sessionId,
          snapshot,
        });
      }
    } catch (err) {
      console.error("[WhiteboardView] Submit error (non-blocking):", err);
    } finally {
      // Show spinner for 1s, then swap to success snackbar for 2s, then advance
      setTimeout(() => {
        setIsFinalSubmitting(false);
        setShowSubmitSuccess(true);
        setTimeout(() => {
          setShowSubmitSuccess(false);
          clearBoard();
          if (isLastQuestion) {
            closeWhiteboard();
          } else {
            if (onSubmit) onSubmit({ isLastQuestion: false, questionIndex });
          }
        }, 2000);
      }, 3000);
    }
  };

  const triggerSubmitButton = () => {
    setShowConfirmationModal(true);
  };

  const SubmitConfirmationModal = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
      style={{ zIndex: 99999 }}
    >
      <div className="bg-white rounded-lg shadow-lg px-[80px] py-[24px]">
        <h3
          className="flex justify-center app-body font-medium mb-4"
          style={{ color: "#333" }}
        >
          Confirmation
        </h3>
        <p
          className="mb-4 text-pretty text-center app-textbox"
          style={{ color: "#666" }}
        >
          {isLastQuestion
            ? "Are you sure you want to submit?"
            : "Are you sure you want to submit this question and move to the next one?"}
        </p>
        <div className="flex justify-center space-x-4 mt-8">
          <button
            className="bg-cancelBgColor btn-size text-white px-6 py-1.5"
            style={{
              background: "#6b7280",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => {
              setShowConfirmationModal(false);
            }}
          >
            <span className="flex items-center">
              <img src={removeWhiteImg} alt="no" className="w-3 h-3 mr-2" /> No
            </span>
          </button>
          <button
            className="bg-btnBgColor btn-size text-white px-6 py-1.5"
            style={{
              background: "#1a1a2e",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontWeight: 600,
            }}
            onClick={() => {
              setShowConfirmationModal(false);

              handleSubmit();
            }}
          >
            <span className="flex items-center">
              <img src={rightImg} alt="yes" className="w-4 h-4 mr-2" /> Yes
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* Pulse keyframe for urgent timer */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.55; }
        }
      `}</style>
      <div
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#fff",
          overflow: "hidden",
          margin: 0,
          padding: 0,
          zIndex: 200,
        }}
      >
        {/* ── Top bar ───────────────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 20px",
            minHeight: 52,
            background: "#1a1a2e",
            flexShrink: 0,
            gap: 20,
          }}
        >
          {/* Logo omitted — MeetingContainer's global logo div covers this */}
          <img
            src={whiteLogoImg}
            alt="Needle logo"
            style={{ height: 20, objectFit: "contain", flexShrink: 0 }}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              flex: 1,
            }}
          >
            <div
              style={{
                color: "#fff",
                fontWeight: 600,
                fontSize: 14,
                lineHeight: "1.3",
                textAlign: "center",
              }}
              title={currentQuestion?.title}
            >
              {currentQuestion?.title}
              {totalQuestions > 1 && (
                <span
                  style={{
                    color: "#a0aec0",
                    fontWeight: 500,
                    fontSize: 12,
                    marginLeft: 8,
                    whiteSpace: "nowrap",
                  }}
                >
                  (Question {questionIndex + 1} of {totalQuestions})
                </span>
              )}
            </div>
            {/* <button
              onClick={() => setShowQuestionModal(true)}
              style={{
                background: "transparent",
                color: "#c8c8ff",
                border: "1px solid #4a4a7e",
                borderRadius: 8,
                padding: "7px 16px",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                whiteSpace: "nowrap",
              }}
            >
              View Question
            </button> */}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* ── Countdown timer ────────────────────────────────────────────── */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                background: timerIsUrgent
                  ? "rgba(220,38,38,0.15)"
                  : "rgba(255,255,255,0.08)",
                border: `1px solid ${timerIsUrgent ? "#ef4444" : "#4a4a7e"}`,
                borderRadius: 8,
                padding: "5px 14px",
                animation: timerIsUrgent ? "pulse 1s infinite" : "none",
              }}
            >
              {/* Clock icon */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke={timerIsUrgent ? "#ef4444" : "#c8c8ff"}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              <span
                style={{
                  color: timerIsUrgent ? "#ef4444" : "#c8c8ff",
                  fontWeight: 700,
                  fontSize: 14,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: 1,
                }}
              >
                {formatTime(timeLeft)}
              </span>
            </div>

            {/* Clear button — lets interviewer/candidate wipe in one click */}
            <button
              onClick={clearBoard}
              style={{
                background: "transparent",
                color: "#c8c8ff",
                border: "1px solid #4a4a7e",
                borderRadius: 8,
                padding: "7px 16px",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Clear
            </button>
            <button
              onClick={triggerSubmitButton}
              disabled={isSubmitting}
              style={{
                background: isSubmitting ? "#ccc" : "#fff",
                color: "#1a1a2e",
                border: "none",
                borderRadius: 8,
                padding: "8px 24px",
                fontWeight: 700,
                fontSize: 14,
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "background 0.2s",
              }}
            >
              {isSubmitting
                ? "Submitting..."
                : isLastQuestion
                  ? "Submit"
                  : "Next Question"}
            </button>
          </div>
        </div>

        {/* ── Recording error banner (non-blocking) ────────────────────────── */}
        {recordingError && (
          <div
            style={{
              background: "#fff3cd",
              borderBottom: "1px solid #ffc107",
              padding: "8px 24px",
              fontSize: 13,
              color: "#856404",
              flexShrink: 0,
            }}
          >
            ⚠ Recording issue: {recordingError}. Your whiteboard work is still
            saved on submit.
          </div>
        )}

        {/* ── "Click Submit when done" snackbar ────────────────────────────── */}
        {showSnackbar && (
          <div
            style={{
              position: "absolute",
              top: 68,
              left: "50%",
              transform: "translateX(-50%)",
              background: "#2e7d4f",
              color: "#fff",
              padding: "14px 32px",
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 15,
              zIndex: 300,
              whiteSpace: "nowrap",
              boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
            }}
          >
            Please explain your approach and illustrate your solution on the
            whiteboard.Click on Submit when you are done.
          </div>
        )}

        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            width: "100%",
            height: "100%",
            overflow: "hidden",
            position: "relative",
            background: "rgb(26, 26, 46)",
          }}
        >
          {/* ── Left Side: Whiteboard container (captured by html2canvas for video) ── */}
          <div
            ref={whiteboardContainerRef}
            style={{
              flex:
                isQuestionPanelOpen && currentQuestion ? "0 0 70%" : "1 1 100%",
              width: isQuestionPanelOpen && currentQuestion ? "70%" : "100%",
              height: "100%",
              position: "relative",
              background: "#f8f9fa",
              transition:
                "width 350ms cubic-bezier(0.4, 0, 0.2, 1), flex 350ms cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            className="interview-whiteboard"
          >
            {/* ── View Question button (top right corner of whiteboard) ─────── */}
            {!isQuestionPanelOpen && (
              <button
                className="view-question-btn"
                onClick={() => setIsQuestionPanelOpen(true)}
                style={{
                  position: "absolute",
                  top: 14,
                  right: 14,
                  zIndex: 20,
                  background: "#1a1a2e",
                  color: "#c8c8ff",
                  border: "1px solid #4a4a7e",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                  transition: "all 0.2s ease",
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="16" y1="13" x2="8" y2="13" />
                  <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
                View Question
              </button>
            )}

            <Excalidraw
              key={sessionId} // fresh board on every whiteboard open
              excalidrawAPI={(api) => {
                excalidrawAPI.current = api;

                // Flush a whiteboard-reconnect payload that arrived before
                // Excalidraw was ready to receive it.
                if (pendingReconnectElementsRef.current) {
                  const elements = pendingReconnectElementsRef.current;
                  pendingReconnectElementsRef.current = null;
                  isRemoteUpdateRef.current = true;
                  api.updateScene({ elements });
                  requestAnimationFrame(() => {
                    isRemoteUpdateRef.current = false;
                  });
                }
              }}
              onChange={handleChange}
              initialData={{
                elements: [],
                appState: {
                  viewBackgroundColor: "#ffffff",

                  activeTool: {
                    type: "selection",
                    locked: true,
                  },
                },
              }}
              UIOptions={{
                canvasActions: {
                  loadScene: false,
                  saveToActiveFile: false,
                  export: false,
                  saveAsImage: false,
                  clearCanvas: true,
                },
              }}
            />
          </div>

          {currentQuestion && (
            <div
              ref={questionPanelRef}
              style={{
                height: "100%",
                width: isQuestionPanelOpen ? "30%" : "0%",
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                background: "rgb(26, 26, 46)",
                borderLeft: isQuestionPanelOpen
                  ? "1px solid grey"
                  : "0px solid transparent",
                borderTop: "1px solid grey",
                boxSizing: "border-box",
                zIndex: 10,
                overflow: "hidden",
                // Smooth open / close width transition
                transition:
                  "width 350ms cubic-bezier(0.4, 0, 0.2, 1), border-left 350ms ease",
              }}
            >
              {/* Inner Wrapper with fixed layout width to prevent text reflow during slide animation */}
              <div
                style={{
                  width: "30vw",
                  minWidth: "300px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  flexShrink: 0,
                  opacity: isQuestionPanelOpen ? 1 : 0,
                  transition: "opacity 200ms ease",
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: "10px 14px",
                    background: "rgb(26, 26, 46)",
                    fontWeight: "bold",
                    fontSize: 14,
                    color: "#fff",
                    flexShrink: 0,
                    borderBottom: "1px solid #444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 6,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: "bold",
                        color: "#fff",
                        lineHeight: "1.4",
                        wordBreak: "break-word",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {"Question"}
                    </span>
                  </div>

                  <Tippy
                    content={
                      isPinned
                        ? "Unpin to auto-hide Question Panel when you click outside Question Panel"
                        : "Pin to keep Question Panel visible"
                    }
                    placement="bottom"
                    animation="shift-away"
                    duration={[150, 100]}
                    arrow={true}
                    delay={[200, 0]}
                    theme="custom-white"
                  >
                    <button
                      onClick={() => setIsPinned(!isPinned)}
                      style={{
                        background: isPinned
                          ? "rgba(16, 185, 129, 0.2)"
                          : "transparent",
                        border: `1px solid ${isPinned ? "#ffff" : "#555"}`,
                        borderRadius: 6,
                        padding: "4px 8px",
                        cursor: "pointer",
                        color: isPinned ? "#ffff" : "#a0aec0",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        fontSize: 12,
                        fontWeight: 600,
                        transition: "all 0.2s",
                        flexShrink: 0,
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill={isPinned ? "currentColor" : "none"}
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <line x1="12" y1="17" x2="12" y2="22" />
                        <path d="M5 17h14v-2l-3-3V5h1V3H7v2h1v7L6 15v2z" />
                      </svg>

                      {/* {isPinned ? "Pinned" : "Pin"} */}
                    </button>
                  </Tippy>
                </div>

                {/* Scrollable question body */}
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    padding: 14,
                    background: "rgb(26, 26, 46)",
                    color: "#e2e8f0",
                    fontSize: 14,
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.6,
                    userSelect: "none",
                    overflowX: "hidden",
                  }}
                >
                  {currentQuestion.question || currentQuestion.title || ""}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showQuestionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#1a1a2e",
              border: "1px solid #4a4a7e",
              borderRadius: 12,
              width: "90%",
              maxWidth: "650px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 10px 25px rgba(0,0,0,0.4)",
              overflow: "hidden",
              fontFamily: "Inter, system-ui, sans-serif",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "16px 20px",
                borderBottom: "1px solid #2d2d4e",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "#fff",
                  fontSize: 16,
                  fontWeight: 600,
                }}
              >
                Question Details
              </h3>
              <button
                onClick={() => setShowQuestionModal(false)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#a0aec0",
                  cursor: "pointer",
                  fontSize: 24,
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                &times;
              </button>
            </div>

            {/* Modal Content */}
            <div
              style={{
                padding: "20px",
                overflowY: "auto",
                color: "#cbd5e0",
                fontSize: 14,
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
                userSelect: "none",
              }}
            >
              <div style={{ color: "#e2e8f0" }}>
                {currentQuestion?.question}
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: "12px 20px",
                borderTop: "1px solid #2d2d4e",
                background: "#161626",
              }}
            >
              <button
                onClick={() => setShowQuestionModal(false)}
                style={{
                  background: "transparent",
                  color: "#c8c8ff",
                  border: "1px solid #4a4a7e",
                  borderRadius: 8,
                  padding: "7px 16px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmationModal && SubmitConfirmationModal}
      {finalSubmitting && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-70"
          style={{ zIndex: 999999 }}
        >
          <div className="text-white text-xl font-semibold flex items-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-6 w-6 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Submitting, please wait...
          </div>
        </div>
      )}
      {showSubmitSuccess && (
        <div
          style={{
            position: "fixed",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 999999,
            background: "#2e7d4f",
            color: "#fff",
            borderRadius: 10,
            padding: "12px 28px",
            fontSize: 15,
            fontWeight: 600,
            boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            gap: 10,
            animation: "fadeInDown 0.3s ease",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          Your response has been submitted!
        </div>
      )}
      <style>{`
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateX(-50%) translateY(-12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </>
  );
}
